import type { FC } from "react";

/**
 * A glanceable "this one reaches outside" marker for template and instance
 * cards.
 *
 * Two decisions worth stating. It names the server when there is exactly one,
 * because a name answers the question a reader actually has ("which?") in the
 * same space a count would waste on the answer they can already see. And it
 * falls back to a count beyond that, because three names is no longer a glance,
 * and the full list is one hover away.
 *
 * It renders nothing when there is nothing attached. An empty marker on every
 * card would cost every reader attention to convey absence, which the absence
 * already conveys.
 */
function compactLabel(
  compact: boolean,
  count: number,
  firstName: string | undefined,
): string {
  if (compact) {
    // A lone server needs no number; more than one does.
    return count > 1 ? String(count) : "";
  }
  return count === 1 ? (firstName ?? "") : `${count} mcp`;
}

export const McpServerChip: FC<{
  /** Resolved server ids, in the order the actor or template carries them. */
  serverIds: readonly string[];
  /** Maps a server id to its display name, for naming one and listing the rest. */
  serverNames?: ReadonlyMap<string, string>;
  /** True when this instance overrides its template's set. */
  overridden?: boolean;
  /**
   * Glyph and count only, for the instance card, whose chip row already carries
   * a level chip and a model chip. Spelling the name there pushed the row onto a
   * second line and made those cards 25px taller than their neighbours, and
   * uneven cards in a grid are worse than an unnamed server: presence stays
   * glanceable, identity moves to the tooltip and the inspector.
   */
  compact?: boolean;
}> = ({ serverIds, serverNames, overridden = false, compact = false }) => {
  if (serverIds.length === 0) {
    return null;
  }
  const names = serverIds.map((id) => serverNames?.get(id) ?? id);
  const label = compactLabel(compact, serverIds.length, names[0]);
  return (
    <span
      className={
        compact
          ? // Bare glyph, no box: a bordered chip on the name line made the card
            // 4.5px taller than its neighbours, and matching neighbours is the
            // whole point of moving it here.
            "shrink-0 font-mono text-[10px] leading-none text-sky-600 dark:text-sky-400"
          : "border border-sky-200 bg-sky-50 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-sky-700 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-300"
      }
      title={`MCP ${serverIds.length === 1 ? "server" : "servers"}: ${names.join(", ")}${
        overridden ? " (set on this actor, not inherited)" : ""
      }`}
    >
      {/* The glyph carries "external tools" so the text can spend itself on
          which ones. A dot marks an override, matching how the other chips on
          these cards distinguish an instance's own setting from its template's. */}
      ⬡{label ? ` ${label}` : ""}
      {overridden && <span className="ml-1 opacity-70">•</span>}
    </span>
  );
};
